import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardList, ChevronDown, ChevronUp, CheckCircle2, Clock, Loader2, Calendar, User } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import api from '../lib/api';
import { toast } from 'sonner';

/**
 * Contextual task panel shown at the top of a module page.
 * Lists tasks (a_faire + en_cours) assigned to the current user for this module.
 * Allows quick status update without leaving the module page.
 *
 * Props:
 *  - moduleKey: string  (e.g. 'properties', 'clients'...)
 *  - moduleLabel: string  (display label, e.g. 'Biens')
 */
const ModuleTasksPanel = ({ moduleKey, moduleLabel }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  const fetchTasks = useCallback(async () => {
    if (!moduleKey) return;
    setLoading(true);
    try {
      const res = await api.get(`/tasks?module=${moduleKey}`);
      // Only show pending tasks (a_faire / en_cours) to keep panel compact
      setTasks((res.data || []).filter(t => t.status !== 'termine'));
    } catch {
      // silent — module panel is non-blocking
    } finally {
      setLoading(false);
    }
  }, [moduleKey]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const handleQuickUpdate = async (taskId, newStatus) => {
    try {
      await api.put(`/tasks/${taskId}`, { status: newStatus });
      toast.success(newStatus === 'termine' ? 'Tache terminee' : 'Tache en cours');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  if (loading && tasks.length === 0) return null;
  if (!loading && tasks.length === 0) return null; // hide panel when no pending tasks

  const urgentCount = tasks.filter(t => t.priority === 'urgente').length;
  const overdueCount = tasks.filter(t => t.due_date && new Date(t.due_date) < new Date()).length;

  return (
    <Card className="border-l-4 border-l-[#D4A745] bg-gradient-to-r from-[#1B3A4B]/[0.02] to-transparent" data-testid={`module-tasks-${moduleKey}`}>
      <CardContent className="p-4">
        <button
          type="button"
          className="w-full flex items-center justify-between gap-3 text-left"
          onClick={() => setCollapsed(c => !c)}
          data-testid={`module-tasks-toggle-${moduleKey}`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-[#1B3A4B] text-[#D4A745] flex items-center justify-center flex-shrink-0">
              <ClipboardList className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-sm text-[#1B3A4B] truncate">
                Mes taches sur {moduleLabel}
                <Badge className="ml-2 bg-[#1B3A4B] text-white text-xs">{tasks.length}</Badge>
                {urgentCount > 0 && <Badge className="ml-1 bg-red-100 text-red-700 text-xs">{urgentCount} urgente{urgentCount > 1 ? 's' : ''}</Badge>}
                {overdueCount > 0 && <Badge className="ml-1 bg-amber-100 text-amber-700 text-xs">{overdueCount} en retard</Badge>}
              </p>
              <p className="text-xs text-gray-500">Cliquez pour {collapsed ? 'developper' : 'reduire'} la liste</p>
            </div>
          </div>
          {collapsed ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />}
        </button>

        {!collapsed && (
          <div className="mt-3 pt-3 border-t space-y-2">
            {tasks.slice(0, 5).map(task => {
              const isOverdue = task.due_date && task.status !== 'termine' && new Date(task.due_date) < new Date();
              return (
                <div key={task.id} className="flex items-center justify-between gap-2 p-2 rounded-md bg-white border hover:border-[#1B3A4B]/30 transition-colors" data-testid={`module-task-${task.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-gray-900 truncate">{task.title}</p>
                      {task.priority === 'urgente' && <Badge className="bg-red-100 text-red-700 text-[10px] py-0">Urgente</Badge>}
                      {task.status === 'en_cours' && <Badge className="bg-blue-100 text-blue-700 text-[10px] py-0">En cours</Badge>}
                      {isOverdue && <Badge className="bg-amber-100 text-amber-700 text-[10px] py-0">En retard</Badge>}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-[11px] text-gray-500">
                      <span className="flex items-center gap-1"><User className="w-3 h-3" />{task.assigned_to_name}</span>
                      {task.due_date && (
                        <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
                          <Calendar className="w-3 h-3" />{new Date(task.due_date).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {task.status === 'a_faire' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-blue-600 hover:bg-blue-50"
                        onClick={() => handleQuickUpdate(task.id, 'en_cours')}
                        title="Demarrer"
                        data-testid={`task-start-${task.id}`}
                      >
                        <Loader2 className="w-3.5 h-3.5 mr-1" /> Demarrer
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-green-600 hover:bg-green-50"
                      onClick={() => handleQuickUpdate(task.id, 'termine')}
                      title="Marquer comme termine"
                      data-testid={`task-done-${task.id}`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Terminer
                    </Button>
                  </div>
                </div>
              );
            })}
            {tasks.length > 5 && (
              <a href="/tasks" className="block text-center text-xs text-[#1B3A4B] underline pt-1">
                Voir les {tasks.length - 5} autre{tasks.length - 5 > 1 ? 's' : ''} taches sur cette page
              </a>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ModuleTasksPanel;
